import { Dispatch, SetStateAction, useContext, useEffect, useEffectEvent, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Button, DialogActions, DialogContent, Grid } from '@mui/material'
import { Form, Formik, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { AppContext, useHost } from '../../providers/app-provider'
import Api from '../../utils/api'
import DialogWithTitle from '../dialog-with-title'
import FormikDropdown from '../formik-dropdown'

import useStyles from './index.styles'

interface IProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  currentChargeableValue: string
  nodeId: number
  setOrderRefreshCounter: Dispatch<SetStateAction<number>>
}

interface IFormValues {
  chargeable: string
}

const accountStatusOptions = [
  { name: 'Exchange/Upgrade', id: 'Exchange/Upgrade' },
  { name: 'Warranty Override', id: 'Warranty override' },
  { name: 'eCommerce Order', id: 'eCommerce Order' },
  { name: 'Warranty', id: 'Warranty' },
  { name: 'Existing Participant - More Devices', id: 'ExistParticMoreDevices' },
  { name: 'HSB Demo', id: 'HSB Demo' },
  { name: 'New Participant - More Devices', id: 'NewPartic MoreDevices' },
  { name: 'Existing Participant', id: 'Existing Participant' },
  { name: 'Customer Stock', id: 'Customer Stock' },
  { name: 'Replacement', id: 'Replacement' },
  { name: 'Reshipped Order', id: 'Reshipped Order' },
  { name: 'New Participant', id: 'NewParticipant' },
].sort((a, b) => a.name.localeCompare(b.name))

const ChangeOrderDetailsModal = (props: IProps) => {
  const { open, setOpen, currentChargeableValue, nodeId, setOrderRefreshCounter } = props
  const classes = useStyles
  const auth = useAuth()
  const host = useHost()
  const { pageRefreshCounter } = useContext(AppContext)

  const { enqueueSnackbar } = useSnackbar()
  const [orderNode, setOrderNode] = useState<INodeType | null>(null)

  const getOrderNode = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token) return
    const res = await Api.get(`${host}/api/nodes/${nodeId}`)
    if (res.ok) {
      setOrderNode(res.body)
    }
  })
  useEffect(() => {
    if (open) {
      getOrderNode()
    }
  }, [nodeId, open, host, pageRefreshCounter])

  const schema = Yup.object().shape({
    chargeable: Yup.string(),
  })

  const initialValues = {
    chargeable: currentChargeableValue ?? '',
  }

  const handleSubmit = async (values: IFormValues) => {
    if (orderNode) {
      const res = await Api.put(`${host}/api/nodes/${nodeId}`, {
        metadata: {
          ...orderNode?.metadata,
          manual_chargeable: values.chargeable,
        },
      })
      if (!res.ok) {
        enqueueSnackbar('Could not update name', { variant: 'error' })
      } else {
        enqueueSnackbar('Chargeable updated successfully')
        setOpen(false)
        setOrderRefreshCounter((c) => c + 1)
      }
    } else {
      enqueueSnackbar('Could not retrieve node details, update is not possible at the moment', { variant: 'error' })
    }
  }

  return (
    <DialogWithTitle setOpen={setOpen} open={open} title="Change Order Details" maxWidth="xs" fullWidth={true}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
        {({ isValid }: FormikProps<IFormValues>) => (
          <Form>
            <DialogContent>
              <Grid container={true} size={12}>
                <FormikDropdown name="chargeable" label="Chargeable" items={accountStatusOptions} required={false} size="medium" />
              </Grid>
            </DialogContent>

            <DialogActions sx={classes.changeModalActions}>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>

              <Button variant="contained" type="submit" disabled={!isValid}>
                Update
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogWithTitle>
  )
}

export default ChangeOrderDetailsModal
